import type {
  BatteryConcertActivityDto,
  BatteryConcertLoanDocumentStatus,
  BatteryConcertApplicationDto,
  BatteryConcertStipulationStatus,
  BatteryConcertTermKey,
  BatteryConcertTermsDto,
} from "~/api/dto/battery-concert-application.dto"

import { formatUsdExact } from "../../../BatteryOnlyProposal/view-helpers"

/**
 * How a Concert application reads to a seller, for every proposal kind: the
 * outcome of the server's status, the loan terms, stipulations, loan
 * documents and activity. The gateway serves both kinds from one Concert
 * service, so one reading fits both; only the offer's noun differs.
 */

export const CONCERT_EYEBROW = `Concert`
export const CONCERT_STAGES = [
  `Submitted`,
  `Decision`,
  `Loan documents`,
] as const

/**
 * Where the application sits on the Concert journey. Stage is the step in
 * progress, so the steps before it read as complete.
 */
export const concertStage = (
  application: BatteryConcertApplicationDto,
): 0 | 1 | 2 => {
  // Any document activity, including a failure, means the journey has reached
  // its last step; the section beneath says how that step is going.
  if (application.loanDocuments.status !== `none`) return 2
  const decided = [
    `approved`,
    `approved_with_stipulations`,
    `denied`,
    `cancelled`,
  ].includes(application.status)
  return decided ? 1 : 0
}
export const LOAN_DOCUMENTS_HEADING = `Loan documents`
export const GENERATE_LOAN_DOCUMENTS_LABEL = `Generate loan documents`
export const GENERATE_LOAN_DOCUMENTS_BUSY_LABEL = `Generating loan documents`
export const SEND_LOAN_DOCUMENTS_LABEL = `Send for signature`
export const SEND_LOAN_DOCUMENTS_BUSY_LABEL = `Sending for signature`
export const SEND_LOAN_DOCUMENTS_BY_EMAIL_LABEL = `Send by email`
export const SIGN_LOAN_DOCUMENTS_IN_PERSON_LABEL = `Sign in person`
export const SIGN_LOAN_DOCUMENTS_IN_PERSON_BUSY_LABEL = `Starting in-person signing`
/** `sent` where the seller may have chosen in-person signing rather than email. */
export const LOAN_DOCUMENTS_SENT_EITHER_WAY_DETAIL = `Concert sent the loan documents for the homeowner's signature, by email or in person. This screen updates once they sign.`

export const LOAN_DOCUMENT_STATUS_COPY: Record<
  BatteryConcertLoanDocumentStatus,
  { label: string; detail: string; tone: ConcertOutcomeTone }
> = {
  none: {
    label: `Not generated`,
    detail: `Concert has not built the signable loan documents yet.`,
    tone: `muted`,
  },
  generating: {
    label: `Generating`,
    detail: `Concert is building the loan documents. This screen refreshes when they are ready.`,
    tone: `gold`,
  },
  generated: {
    label: `Generated`,
    detail: `Concert has built the loan documents. Send them when the homeowner is ready to sign.`,
    tone: `success`,
  },
  failed: {
    label: `Generation failed`,
    detail: `Concert could not build the loan documents. Try again, and contact support if it keeps failing.`,
    tone: `danger`,
  },
  sending: {
    label: `Sending`,
    detail: `The signature request is on its way to the homeowner.`,
    tone: `gold`,
  },
  sent: {
    label: `Sent for signature`,
    detail: `Concert emailed the loan documents to the homeowner. This screen updates once they sign.`,
    tone: `success`,
  },
  signed: {
    label: `Signed`,
    detail: `The homeowner signed the loan documents. The Powur installation contract has its own signature status.`,
    tone: `success`,
  },
  send_failed: {
    label: `Send failed`,
    detail: `Concert could not email the loan documents. Try again, and contact support if it keeps failing.`,
    tone: `danger`,
  },
}

/** Concert has the package but has not finished building it. */
export const LOAN_DOCUMENTS_PREPARING_DETAIL = `Concert is still preparing the documents. This screen refreshes when they are ready to send.`
export const RETRY_APPLICATION_LABEL = `Retry application`
export const SUBMIT_DOCUMENTS_FOR_REVIEW_LABEL = `Submit documents for review`
export const UPLOAD_DOCUMENT_LABEL = `Upload document`
export const REPLACE_DOCUMENT_LABEL = `Replace document`
export const CONCERT_SUPPORT_GUIDANCE = `Contact Powur support before taking any further action on this application.`

export type ConcertOutcomeTone = `danger` | `gold` | `muted` | `success`

export type ConcertOutcome = {
  badge: string
  tone: ConcertOutcomeTone
  title: string
  detail: string
  /** Support is the only next step; nothing on the proposal fixes it. */
  supportNeeded: boolean
}

const passes = (status: string | null): boolean =>
  [`pass`, `approved`].includes(status?.toLowerCase() ?? ``)

/**
 * One reading of the server's status, submission state, eligibility, and
 * review flag. Credit passing alone is never presented as approval.
 */
export const concertApplicationOutcome = (
  application: BatteryConcertApplicationDto,
  /** What the offer finances: Battery, Solar. */
  offerNoun: string,
): ConcertOutcome => {
  const { submission, status, eligibility, reviewNeeded, changedTerms } =
    application

  if (submission === `failed`) {
    return {
      badge: `Not submitted`,
      tone: `danger`,
      title: `Application not submitted`,
      detail: `Concert rejected the application before running credit. Correct the applicant details and submit again.`,
      supportNeeded: false,
    }
  }

  if (submission !== `accepted`) {
    return {
      badge: `Awaiting confirmation`,
      tone: `gold`,
      title: `Submission awaiting confirmation`,
      detail: `Vision has not confirmed whether Concert received this application. Do not submit it again; status refreshes once Concert responds.`,
      supportNeeded: false,
    }
  }

  switch (status) {
    case `denied`:
      return {
        badge: `Declined`,
        tone: `danger`,
        title: `Application declined`,
        detail: `Concert declined this application. Financing edits stay locked on this proposal.`,
        supportNeeded: true,
      }
    case `cancelled`:
      return {
        badge: `Cancelled`,
        tone: `muted`,
        title: `Application cancelled`,
        detail: `Concert reports this application as cancelled. Financing edits stay locked on this proposal.`,
        supportNeeded: true,
      }
    case `approved`:
    case `approved_with_stipulations`: {
      if (reviewNeeded != null) {
        return {
          badge: `Review needed`,
          tone: `gold`,
          title: `Approved, but the terms need review`,
          detail:
            reviewNeeded === `APPROVED_PRIMARY_BATTERY_OFFER_MISSING`
              ? `Concert approved the application but returned no eligible approved primary ${offerNoun} offer. Vision will not choose a replacement offer or estimate a payment.`
              : `Concert approved the application but the approved ${offerNoun} terms could not be read yet. Refresh status; if this persists, the terms need support review.`,
          supportNeeded: true,
        }
      }
      if (status === `approved_with_stipulations`) {
        return {
          badge: `Approved with conditions`,
          tone: `success`,
          title: `Approved with conditions`,
          detail: `Concert approved this application pending the stipulations below. Upload each supporting document, then submit them for review.`,
          supportNeeded: false,
        }
      }
      return {
        badge: `Approved`,
        tone: `success`,
        title: `Approved`,
        detail:
          changedTerms.length > 0
            ? `Concert approved different terms than the submitted Quote. The approved terms below are authoritative and already reflected in the proposal.`
            : `Concert approved the submitted terms. The proposal financial summary reflects the approved payment.`,
        supportNeeded: false,
      }
    }
    case `applied`:
    case `incomplete`:
    default: {
      const creditPassed = passes(eligibility.creditStatus)
      if (creditPassed && !passes(eligibility.overallStatus)) {
        return {
          badge: `Credit passed`,
          tone: `gold`,
          title: `Credit passed, eligibility still pending`,
          detail: `Concert passed the credit check, but the overall eligibility decision is incomplete${
            eligibility.titleStatus
              ? ` (title check: ${eligibility.titleStatus})`
              : ``
          }. This is not a full approval yet.`,
          supportNeeded: false,
        }
      }
      return {
        badge: `Pending`,
        tone: `muted`,
        title: `Waiting on Concert`,
        detail: `Concert is reviewing the application. Status refreshes automatically when a decision arrives.`,
        supportNeeded: false,
      }
    }
  }
}

export const OUTCOME_TONE_CLASS: Record<ConcertOutcomeTone, string> = {
  danger: `border-bp-danger/40 bg-bp-danger/10 text-bp-danger`,
  gold: `border-bp-gold/40 bg-bp-gold/10 text-bp-gold`,
  muted: `border-bp-border-strong bg-bp-elevated-2 text-bp-text-muted`,
  success: `border-bp-success/40 bg-bp-success/10 text-bp-success`,
}

const monthlyUsd = (amount: number): string => `${formatUsdExact(amount)}/mo`

const TERM_LABEL: Record<BatteryConcertTermKey, string> = {
  amount: `Financed amount`,
  termYears: `Term`,
  apr: `APR`,
  monthlyPayment: `Monthly payment`,
}

export const formatConcertTerm = (
  key: BatteryConcertTermKey,
  terms: BatteryConcertTermsDto,
): string => {
  switch (key) {
    case `amount`:
      return formatUsdExact(terms.amount)
    case `termYears`:
      return `${terms.termYears}-year`
    case `apr`:
      return `${terms.apr.toFixed(2)}%`
    case `monthlyPayment`:
      return monthlyUsd(terms.monthlyPayment)
  }
}

export type ConcertTermsRow = {
  key: BatteryConcertTermKey
  label: string
  submitted: string
  approved: string | null
  changed: boolean
}

export const TERM_KEYS: readonly BatteryConcertTermKey[] = [
  `amount`,
  `termYears`,
  `apr`,
  `monthlyPayment`,
]

/** Submitted beside approved, with the server's changed keys highlighted. */
export const concertTermsRows = (
  application: BatteryConcertApplicationDto,
): ConcertTermsRow[] =>
  TERM_KEYS.map((key) => ({
    key,
    label: TERM_LABEL[key],
    submitted: formatConcertTerm(key, application.submittedTerms),
    approved:
      application.approvedTerms != null
        ? formatConcertTerm(key, application.approvedTerms)
        : null,
    changed:
      application.approvedTerms != null &&
      application.changedTerms.includes(key),
  }))

export const STIPULATION_STATUS_LABEL: Record<
  BatteryConcertStipulationStatus,
  string
> = {
  open: `Open`,
  submitted: `Submitted`,
  completed: `Completed`,
  rejected: `Rejected`,
  waived: `Waived`,
}

export const isResolvedStipulation = (
  status: BatteryConcertStipulationStatus,
): boolean => status === `completed` || status === `waived`

export const STIPULATION_PILL_CLASS: Record<
  BatteryConcertStipulationStatus,
  string
> = {
  open: `border-bp-border-strong text-bp-text`,
  submitted: `border-bp-accent/40 bg-bp-accent/10 text-bp-text`,
  completed: `border-bp-success/40 bg-bp-success/10 text-bp-success`,
  rejected: `border-bp-danger/40 bg-bp-danger/10 text-bp-danger`,
  waived: `border-bp-border text-bp-text-muted`,
}

const DECISION_LABEL: Record<string, string> = {
  approved: `Concert decision: approved`,
  approved_with_stipulations: `Concert decision: approved with conditions`,
  denied: `Concert decision: declined`,
  cancelled: `Concert decision: cancelled`,
  applied: `Concert update: application under review`,
  incomplete: `Concert update: application under review`,
}

export const concertActivityLabel = (
  event: BatteryConcertActivityDto,
): string => {
  switch (event.type) {
    case `application_submitted`:
      return `Application submitted`
    case `provider_decision`: {
      const action = event.details?.action
      if (action === `supporting_document_uploaded`) {
        return `Supporting document uploaded`
      }
      if (action === `supporting_documents_submitted`) {
        return `Documents submitted for review`
      }
      const outcome = event.details?.outcome
      return typeof outcome === `string`
        ? (DECISION_LABEL[outcome] ?? `Concert decision: ${outcome}`)
        : `Concert update`
    }
    case `loan_docs_generated`:
      return `Loan documents generated`
    case `loan_docs_sent`:
      return `Loan documents sent`
    case `customer_signed`:
      return `Customer signed`
  }
}

export const concertDateLabel = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return `—`
  }
  return new Intl.DateTimeFormat(`en-US`, {
    year: `numeric`,
    month: `short`,
    day: `numeric`,
  }).format(date)
}
